import { useRef, useState } from 'react'

export const useAudioRecording = (onAudioGenerated: (audio: Blob) => void, maxRecordingTime = 3_000) => {
  const [isRecording, setIsRecording] = useState(false)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const progressEndTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const startRecordingRef = useRef(0)
  const streamRef = useRef<MediaStream | null>(null)

  const stopMediaRecorder = () => {
    audioChunksRef.current = []
    recorderRef.current = null
    const tracks = streamRef.current?.getTracks() || []
    tracks.forEach(track => {
      track.stop()
    })
    streamRef.current = null
  }

  const startRecording = async () => {
    // this may happened if to click recording second time right after granting permissions
    if (progressEndTimer.current) return stopRecording()

    // oxlint-disable-next-line react/purity
    startRecordingRef.current = Date.now()

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.error({ message: 'Media devices API is not available. This feature requires HTTPS or a secure context.' })

      return
    }

    const stream = await navigator.mediaDevices
      .getUserMedia({ audio: true })
      .catch(error => console.error({ message: 'Error accessing microphone', error }))
    if (!stream) {
      console.error({ message: "Can't initiate an audio stream" })

      return
    }

    streamRef.current = stream

    const mediaRecorder = new MediaRecorder(stream)
    mediaRecorder.ondataavailable = e => {
      audioChunksRef.current.push(e.data)
    }
    mediaRecorder.onstop = () => {
      // Use the actual mimeType from MediaRecorder instead of hardcoding
      // Different browsers/devices use different formats:
      // - Chrome/Edge: audio/webm; codecs=opus
      // - Firefox: audio/ogg; codecs=opus
      // - Safari/iOS: audio/mp4 or audio/aac
      const mimeType = mediaRecorder.mimeType || 'audio/webm; codecs=opus'
      const audioBlob = new Blob(audioChunksRef.current, { type: mimeType })
      onAudioGenerated(audioBlob)
      stopMediaRecorder()
    }
    recorderRef.current = mediaRecorder
    mediaRecorder.start()
    setIsRecording(true)

    progressEndTimer.current = setTimeout(() => {
      void stopRecording()
      clearProgressTimer()
    }, maxRecordingTime)
  }

  const cancelRecording = () => {
    if (!recorderRef.current) return

    clearProgressTimer()

    recorderRef.current.ondataavailable = null
    recorderRef.current.onstop = null
    recorderRef.current.stop()
    recorderRef.current = null

    stopMediaRecorder()
    setIsRecording(false)
  }

  const clearProgressTimer = () => {
    if (!progressEndTimer.current) return

    clearTimeout(progressEndTimer.current)
    progressEndTimer.current = null
  }

  const stopRecording = async () => {
    clearProgressTimer()

    setIsRecording(false)
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop()
  }

  return { isRecording, startRecording, stopRecording, cancelRecording }
}
